import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, PlugZap, Rocket, ArrowRight } from "lucide-react";

const integrateItems = [
  "Keep your current PMS",
  "Add Asha AI + Communication Suite on top",
  "Available for Dentrix, Eaglesoft, Open Dental",
  "Go live without a migration",
];

const switchItems = [
  "Migrate onto Asha's AI-native PMS",
  "Everything included: PMS + AI + Comms",
  "Best experience for your staff — purpose-built for dental",
  "Much more cost-effective at scale",
];

const EngagementOptionsSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1180px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-600 text-sm font-semibold mb-4 border border-blue-100/60 shadow-sm">
            <Sparkles className="w-4 h-4" />
            <span>Two Ways to Work With Us</span>
          </div>
          <h2 className="text-4xl lg:text-[48px] font-bold text-slate-900 tracking-tight leading-tight">
            Pick the engagement model{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              that fits your practice.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-2xl mx-auto leading-relaxed">
            Keep your existing PMS and layer our AI on top — or switch to Asha's AI-native PMS for
            the lowest cost and highest efficiency.
          </p>
        </motion.div>

        {/* Side-by-side cards */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Integrate option */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="relative rounded-[24px] overflow-hidden border border-white/80 bg-white/90 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.1)] p-8 flex flex-col"
          >
            <div
              className="absolute top-0 left-0 right-0 h-[4px]"
              style={{ background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)" }}
            />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <PlugZap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-blue-500 uppercase tracking-wider">
                  Option 1
                </p>
                <h3 className="text-[22px] font-bold text-slate-900">
                  Integrate with your existing PMS
                </h3>
              </div>
            </div>
            <p className="text-[15px] text-slate-700 leading-relaxed mb-5">
              You stay on the PMS you already know. We plug our AI workforce and communication
              suite directly into it — no migration required.
            </p>
            <div className="space-y-2.5 flex-1">
              {integrateItems.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-[14px] text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Switch option */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="relative rounded-[24px] overflow-hidden border border-indigo-100 bg-gradient-to-br from-indigo-50/60 via-white to-blue-50/60 shadow-[0_20px_60px_-20px_rgba(99,102,241,0.25)] p-8 flex flex-col"
          >
            <div
              className="absolute top-0 left-0 right-0 h-[4px]"
              style={{ background: "linear-gradient(90deg, #6366f1 0%, #3b82f6 100%)" }}
            />
            <div className="absolute top-4 right-4">
              <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-widest">
                Recommended
              </span>
            </div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-indigo-500 uppercase tracking-wider">
                  Option 2
                </p>
                <h3 className="text-[22px] font-bold text-slate-900">
                  Transition onto Asha's PMS
                </h3>
              </div>
            </div>
            <p className="text-[15px] text-slate-700 leading-relaxed mb-5">
              Move onto our AI-native PMS. Our agents don't need excessive reads and writes across
              systems — so the AI cost drops dramatically as you scale.
            </p>
            <div className="space-y-2.5 flex-1">
              {switchItems.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-indigo-600" />
                  </div>
                  <span className="text-[14px] text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-6 flex items-center gap-2 text-slate-600 text-[14px] font-medium">
          <ArrowRight className="w-4 h-4" />
          <span>Pricing breakdown on the next slides. Integrations listed separately.</span>
        </div>
      </div>
    </div>
  );
};

export default EngagementOptionsSlide;
